/**
 * FOS Explorer Page
 *
 * Main container for FOS corpus exploration with:
 * - Semantic search with natural language queries
 * - Faceted filtering (outcome, product, company, compensation, date)
 * - Results list with decision cards
 * - Decision detail modal
 * - Embedding visualization with UMAP projections (Phase 2)
 * - Cluster discovery and navigation
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useFOSSearch, useFOSFacets, useFOSStats, useFOSProjections, useFOSSignalProjections } from '../../../hooks/useFOSExplorer';
import { FOSSearchParams, FOSDecisionCard, refreshFOSSignalProjections } from '../../../utils/api';
import FOSSemanticSearch from './FOSSemanticSearch';
import FOSFacetFilters from './FOSFacetFilters';
import FOSResultsList from './FOSResultsList';
import FOSDecisionPanel from './FOSDecisionPanel';
import FOSEmbeddingCanvas from './FOSEmbeddingCanvas';
import FOSClusterLegend from './FOSClusterLegend';
import ClusterDetailPanel from './ClusterDetailPanel';
import { Button } from '../../ui/button';
import { Skeleton } from '../../ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '../../ui/toggle-group';
import IconWrapper from '../../IconWrapper';
import { FiDatabase, FiSearch, FiGrid, FiList, FiBookOpen, FiRefreshCw, FiEdit2 } from 'react-icons/fi';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import { cn } from '../../../lib/utils';
import { useNavigation } from '../../../contexts/NavigationContext';
import { ReportBlock } from '../../../types/report-builder';
import ReportCanvas from '../../v4/reports/ReportCanvas';
import ReportChatPanel from '../../v4/reports/ReportChatPanel';

interface FOSExplorerPageProps {
  className?: string;
}

const FOSExplorerPage: React.FC<FOSExplorerPageProps> = ({ className = '' }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { navigateWithReferrer } = useNavigation();

  // View mode state
  const [viewMode, setViewMode] = useState<'search' | 'canvas' | 'visualize'>(
    searchParams.get('view') === 'visualize' ? 'visualize' : searchParams.get('view') === 'canvas' ? 'canvas' : 'search'
  );
  const [colorBy, setColorBy] = useState<'outcome' | 'cluster'>('outcome');
  const [showSignals, setShowSignals] = useState(false);
  const [isRefreshingSignals, setIsRefreshingSignals] = useState(false);
  const [refreshNotice, setRefreshNotice] = useState<string | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('q') || '');
  const [debouncedQuery, setDebouncedQuery] = useState<string>(searchQuery);
  const [filters, setFilters] = useState<Partial<FOSSearchParams>>({
    outcome: searchParams.get('outcome') || undefined,
    product_category: searchParams.get('product') || undefined,
    company: searchParams.get('company') || undefined,
    year: searchParams.get('year') ? parseInt(searchParams.get('year')!) : undefined,
  });
  const [page, setPage] = useState(0);
  const pageSize = 20;

  // Selected decision for modal
  const [selectedDecisionId, setSelectedDecisionId] = useState<string | null>(null);

  // Cluster selection for visualization
  const [selectedClusterId, setSelectedClusterId] = useState<number | null>(null);

  // Cluster hover state for highlight/dim effect
  const [hoveredClusterId, setHoveredClusterId] = useState<number | null>(null);

  // Canvas (Analyst Canvas) state
  const [canvasBlocks, setCanvasBlocks] = useState<ReportBlock[]>([]);
  const [isCanvasGenerating, setIsCanvasGenerating] = useState(false);

  const handleCanvasBlocksUpdate = useCallback((newBlocks: ReportBlock[]) => {
    setCanvasBlocks(newBlocks);
  }, []);

  const handleRemoveCanvasBlock = useCallback((blockId: string) => {
    setCanvasBlocks((prev) => prev.filter((b) => b.id !== blockId));
  }, []);

  const handleMoveCanvasBlock = useCallback((blockId: string, direction: 'up' | 'down') => {
    setCanvasBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === blockId);
      if (idx === -1) return prev;
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (targetIdx < 0 || targetIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[targetIdx]] = [next[targetIdx], next[idx]];
      return next;
    });
  }, []);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      setPage(0); // Reset to first page on new search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Build search params
  const searchParamsForQuery: FOSSearchParams | null = debouncedQuery
    ? {
        query: debouncedQuery,
        limit: pageSize,
        offset: page * pageSize,
        ...filters,
        use_semantic: true,
      }
    : null;

  // Fetch data
  const { data: searchResults, isLoading: isSearching, error: searchError } = useFOSSearch(
    searchParamsForQuery,
    { keepPreviousData: true }
  );
  const { data: facets, isLoading: isFacetsLoading } = useFOSFacets();
  const { data: stats, isLoading: isStatsLoading } = useFOSStats();
  const { data: projectionData } = useFOSProjections({ enabled: viewMode === 'visualize' });
  const { data: signalProjectionsData, refetch: refetchSignalProjections, isFetching: isFetchingSignalProjections } = useFOSSignalProjections(30, {
    enabled: viewMode === 'visualize' && showSignals,
  });

  // Update URL params when search/filters change
  // Preserve existing params (like 'tab') that are managed by parent components
  useEffect(() => {
    setSearchParams(
      (prevParams) => {
        const params = new URLSearchParams(prevParams);

        // Set or remove view param
        if (viewMode === 'visualize') {
          params.set('view', 'visualize');
        } else if (viewMode === 'canvas') {
          params.set('view', 'canvas');
        } else {
          params.delete('view');
        }

        // Set or remove search params
        if (debouncedQuery) {
          params.set('q', debouncedQuery);
        } else {
          params.delete('q');
        }

        // Set or remove filter params
        if (filters.outcome) {
          params.set('outcome', filters.outcome);
        } else {
          params.delete('outcome');
        }
        if (filters.product_category) {
          params.set('product', filters.product_category);
        } else {
          params.delete('product');
        }
        if (filters.company) {
          params.set('company', filters.company);
        } else {
          params.delete('company');
        }
        if (filters.year) {
          params.set('year', filters.year.toString());
        } else {
          params.delete('year');
        }

        return params;
      },
      { replace: true }
    );
  }, [viewMode, debouncedQuery, filters, setSearchParams]);

  // Handlers
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleFilterChange = useCallback((newFilters: Partial<FOSSearchParams>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(0);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setPage(0);
  }, []);

  const handleDecisionClick = useCallback((decision: FOSDecisionCard) => {
    setSelectedDecisionId(decision.id);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedDecisionId(null);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSignalClick = useCallback((signalId: string) => {
    navigateWithReferrer(`/v4/records/default/${signalId}`, `${location.pathname}${location.search}`);
  }, [navigateWithReferrer, location.pathname, location.search]);

  const handleRefreshSignals = useCallback(async () => {
    if (isRefreshingSignals) return;
    setIsRefreshingSignals(true);
    setRefreshNotice(null);

    try {
      await refreshFOSSignalProjections(30);
      setRefreshNotice('Refresh queued. New overlays may take a minute.');
      await refetchSignalProjections();
      window.setTimeout(() => {
        refetchSignalProjections();
      }, 5000);
    } catch (err) {
      console.error('[FOS Explorer] Failed to refresh signal projections:', err);
      setRefreshNotice('Failed to refresh signal overlays.');
    } finally {
      setIsRefreshingSignals(false);
      window.setTimeout(() => setRefreshNotice(null), 5000);
    }
  }, [isRefreshingSignals, refetchSignalProjections]);

  const totalPages = searchResults ? Math.ceil(searchResults.total / pageSize) : 0;
  const hasActiveFilters = Object.values(filters).some(v => v !== undefined);

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Header with Stats and View Toggle */}
      <div className="flex-shrink-0 mb-5">
        <div className="flex items-center justify-between gap-3 mb-4">
            {/* View Mode Toggle (left) */}
            <ToggleGroup
              type="single"
              value={viewMode}
              onValueChange={(value: string) => value && setViewMode(value as 'search' | 'canvas' | 'visualize')}
              className="rounded-lg bg-muted/60 p-0.5"
            >
              <ToggleGroupItem
                value="search"
                className={cn(
                  "text-xs px-3 py-1.5 rounded-md gap-1.5",
                  "data-[state=on]:bg-background data-[state=on]:shadow-sm"
                )}
              >
                <IconWrapper icon={FiList} className="h-3.5 w-3.5" />
                Search
              </ToggleGroupItem>
              <ToggleGroupItem
                value="canvas"
                className={cn(
                  "text-xs px-3 py-1.5 rounded-md gap-1.5",
                  "data-[state=on]:bg-background data-[state=on]:shadow-sm"
                )}
              >
                <IconWrapper icon={FiEdit2} className="h-3.5 w-3.5" />
                Canvas
              </ToggleGroupItem>
              <ToggleGroupItem
                value="visualize"
                className={cn(
                  "text-xs px-3 py-1.5 rounded-md gap-1.5",
                  "data-[state=on]:bg-background data-[state=on]:shadow-sm"
                )}
              >
                <IconWrapper icon={FiGrid} className="h-3.5 w-3.5" />
                Explore
              </ToggleGroupItem>
            </ToggleGroup>

            {/* Stats Pills (right) */}
            {isStatsLoading ? (
              <div className="flex gap-2">
                <Skeleton className="h-7 w-28 rounded-full" />
                <Skeleton className="h-7 w-24 rounded-full" />
              </div>
            ) : stats ? (
              <div className="flex items-center gap-2">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-muted/50 border border-border/50">
                  <IconWrapper icon={FiDatabase} className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium text-foreground/80">
                    {stats.searchable_decisions.toLocaleString()}
                  </span>
                </div>
              </div>
            ) : null}
        </div>

        {/* Search Bar - only show in search mode */}
        {viewMode === 'search' && (
          <FOSSemanticSearch
            value={searchQuery}
            onChange={handleSearchChange}
            isSearching={isSearching && !!debouncedQuery}
            placeholder="Search decisions... (e.g., 'PPI mis-selling', 'mortgage fees', 'insurance claim denied')"
          />
        )}
      </div>

      {/* Main Content */}
      {viewMode === 'canvas' ? (
        /* Canvas View - Analyst Canvas with Chat */
        <div className="flex-1 flex min-h-0 overflow-hidden rounded-lg border border-border bg-card shadow-[var(--shadow-card)]">
          <div className="flex-1 flex flex-col bg-white">
            <ReportCanvas
              blocks={canvasBlocks}
              isGenerating={isCanvasGenerating}
              onRemoveBlock={handleRemoveCanvasBlock}
              onMoveBlock={handleMoveCanvasBlock}
              embedded
            />
          </div>
          <div className="w-[340px] flex-shrink-0 border-l border-border/70 bg-card">
            <ReportChatPanel
              onBlocksUpdate={handleCanvasBlocksUpdate}
              onGeneratingChange={setIsCanvasGenerating}
              embedded
            />
          </div>
        </div>
      ) : viewMode === 'search' ? (
        /* Search View - Split Layout */
        <div className="flex-1 flex gap-5 min-h-0 overflow-hidden">
          {/* Left Panel - Filters */}
          <div className="w-56 flex-shrink-0 overflow-y-auto">
            <div className="overflow-hidden rounded-lg border border-border bg-card shadow-[var(--shadow-card)]">
              <div className="px-4 py-3 border-b border-border/40">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">Filters</span>
                  {hasActiveFilters && (
                    <button
                      onClick={handleClearFilters}
                      className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
                    >
                      Clear all
                    </button>
                  )}
                </div>
              </div>
              <FOSFacetFilters
                facets={facets}
                isLoading={isFacetsLoading}
                activeFilters={filters}
                onFilterChange={handleFilterChange}
              />
            </div>
          </div>

          {/* Right Panel - Results */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {/* Results Header */}
            {searchResults && debouncedQuery && (
              <div className="flex-shrink-0 flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">{searchResults.total.toLocaleString()}</span>
                    {' '}results for "{debouncedQuery.length > 100 ? debouncedQuery.substring(0, 100) + '…' : debouncedQuery}"
                  </span>
                </div>
                {/* Pagination info */}
                {totalPages > 1 && (
                  <span className="text-xs text-muted-foreground tabular-nums">
                    Page {page + 1} of {totalPages}
                  </span>
                )}
              </div>
            )}

            {/* Results List */}
            <div className="flex-1 overflow-y-auto">
              {!debouncedQuery ? (
                /* Enhanced Empty State */
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="relative mb-6">
                    {/* Decorative background circle */}
                    <div className="absolute inset-0 -m-4 bg-gradient-to-br from-primary/5 via-purple/5 to-teal/5 rounded-full blur-xl" />
                    <div className="relative flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-muted/80 to-muted/40 border border-border/40">
                      <IconWrapper icon={FiBookOpen} className="h-8 w-8 text-muted-foreground/60" />
                    </div>
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-1">Search FOS Decisions</h3>
                  <p className="text-sm text-muted-foreground text-center max-w-xs mb-6">
                    Use natural language to explore relevant ombudsman decisions from the FOS corpus
                  </p>
                  {/* Example queries */}
                  <div className="flex flex-wrap gap-2 justify-center max-w-md">
                    {['PPI mis-selling', 'mortgage fees', 'insurance claim denied', 'credit card charges'].map((query) => (
                      <button
                        key={query}
                        onClick={() => setSearchQuery(query)}
                        className={cn(
                          "px-3 py-1.5 text-xs rounded-full",
                          "bg-muted/50 hover:bg-muted border border-border/50",
                          "text-muted-foreground hover:text-foreground",
                          "transition-colors duration-150"
                        )}
                      >
                        {query}
                      </button>
                    ))}
                  </div>
                </div>
              ) : searchError ? (
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 mb-4">
                    <IconWrapper icon={FiSearch} className="h-7 w-7 text-destructive/60" />
                  </div>
                  <p className="text-base font-medium text-destructive">Search failed</p>
                  <p className="text-sm text-muted-foreground mt-1">{searchError.message}</p>
                </div>
              ) : (
                <FOSResultsList
                  decisions={searchResults?.decisions || []}
                  isLoading={isSearching}
                  onDecisionClick={handleDecisionClick}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Visualize View - Embedding Canvas with Legend and Detail Panel */
        <div className="flex-1 flex gap-4 min-h-0 overflow-hidden">
          {/* Left Panel - Canvas Controls */}
          <div className="w-56 flex-shrink-0 overflow-y-auto">
            <div className="bg-card rounded-lg border border-border shadow-[var(--shadow-card)]">
              {/* Display Section */}
              <div className="px-4 py-3 border-b border-border/40">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Display</span>
              </div>
              <div className="px-4 py-3 border-b border-border/40">
                <ToggleGroup
                  type="single"
                  value={colorBy}
                  onValueChange={(value: string) => value && setColorBy(value as 'outcome' | 'cluster')}
                  className="w-full"
                >
                  <ToggleGroupItem value="outcome" className="flex-1 text-xs">
                    Outcome
                  </ToggleGroupItem>
                  <ToggleGroupItem value="cluster" className="flex-1 text-xs">
                    Cluster
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>

              {/* Overlays Section */}
              <div className="px-4 py-3 border-b border-border/40">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Overlays</span>
              </div>
              <div className="px-4 py-3 border-b border-border/40">
                <div className="flex items-center justify-between">
                  <Label htmlFor="show-signals" className="text-sm font-medium cursor-pointer">
                    My Signals
                  </Label>
                  <div className="flex items-center gap-2">
                    {showSignals && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={handleRefreshSignals}
                        disabled={isRefreshingSignals || isFetchingSignalProjections}
                        aria-label="Refresh signal overlays"
                      >
                        <IconWrapper
                          icon={FiRefreshCw}
                          className={cn("h-3 w-3", (isRefreshingSignals || isFetchingSignalProjections) && "animate-spin")}
                        />
                      </Button>
                    )}
                    <Switch
                      id="show-signals"
                      checked={showSignals}
                      onCheckedChange={setShowSignals}
                    />
                  </div>
                </div>
                {showSignals && signalProjectionsData && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {signalProjectionsData.signals.length} signals from last {signalProjectionsData.days} days
                  </p>
                )}
                {showSignals && refreshNotice && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {refreshNotice}
                  </p>
                )}
              </div>

              {/* Legend Section */}
              <div className="px-4 py-3 border-b border-border/40">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Legend</span>
              </div>
              <div className="px-4 py-3">
                {projectionData && (
                  <FOSClusterLegend
                    clusters={projectionData.clusters}
                    colorMode={colorBy}
                    selectedClusterId={selectedClusterId}
                    hoveredClusterId={hoveredClusterId}
                    onClusterClick={setSelectedClusterId}
                    onClusterHover={setHoveredClusterId}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Center Panel - Canvas */}
          <div className="flex-1 flex flex-col min-h-0">
            <FOSEmbeddingCanvas
              colorBy={colorBy}
              onPointClick={(decisionId) => setSelectedDecisionId(decisionId)}
              onSignalClick={handleSignalClick}
              signalReferrerPath={`${location.pathname}${location.search}`}
              highlightedDecisionId={selectedDecisionId}
              hoveredClusterId={hoveredClusterId}
              selectedClusterId={selectedClusterId}
              onClusterHover={setHoveredClusterId}
              onClusterSelect={setSelectedClusterId}
              showSignals={showSignals}
              signalProjections={signalProjectionsData?.signals}
              className="w-full h-full rounded-lg border border-border bg-card shadow-[var(--shadow-card)]"
            />
          </div>

          {/* Right Panel - Cluster Detail (Always visible) */}
          <ClusterDetailPanel
            hoveredCluster={projectionData?.clusters.find(c => c.cluster_num === hoveredClusterId) || null}
            selectedCluster={projectionData?.clusters.find(c => c.cluster_num === selectedClusterId) || null}
            decisions={[]} // TODO: Add API endpoint to fetch cluster decisions
            isLoading={false}
            onDecisionSelect={(decisionId) => setSelectedDecisionId(decisionId)}
            onClearSelection={() => setSelectedClusterId(null)}
          />
        </div>
      )}

      {/* Decision Detail Panel */}
      <FOSDecisionPanel
        decisionId={selectedDecisionId}
        isOpen={!!selectedDecisionId}
        onClose={handleCloseModal}
        mode="sheet"
      />
    </div>
  );
};

export default FOSExplorerPage;
